import React, { useState, useCallback } from 'react';
import { DropdownMenuItem } from '../../ui/dropdown-menu';
import { useLazyCommand, useDomainEvent } from '@soap/modules';
import { translate } from '@soap/modules';
import { Trash2, X } from 'lucide-react';

interface DeleteStudentDialogProps {
  studentId: string;
  studentName: string;
}

export function DeleteStudentDialog({ studentId, studentName }: DeleteStudentDialogProps) {
  const [open, setOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [sendDeleteCommand] = useLazyCommand('C229v1_DeleteStudent');

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await sendDeleteCommand({
        C229_StudentId: studentId
      });
      close();
    } catch (error) {
      console.error('Error deleting student:', error);
      setIsDeleting(false);
    }
  };

  const triggerCloseContextMenu = useDomainEvent({
    eventName: "close-context-menu",
  });

  const close = useCallback(() => {
    setOpen(false)
    triggerCloseContextMenu();
  },[setOpen]);

  if (!open) {
    return (
      <DropdownMenuItem
        className="w-full justify-start text-destructive-action hover:text-destructive-action-dark"
        data-dialog-item
        onClick={() => setOpen(true)}
      >
        <Trash2 className="w-4 h-4 mr-2" />
        {translate("Delete")}
      </DropdownMenuItem>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-2xl font-bold text-gray-900">{translate("Delete Student")}</h2>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-4">
            <div className="bg-destructive-action/10 p-4 rounded-lg border border-destructive-action/30">
              <p className="text-sm text-destructive-action-dark">
                {`${translate("This action cannot be undone")}.`}
              </p>
              <p className="text-sm text-destructive-action-dark mt-2">
                {translate("Please confirm you want to delete the student")} <strong>{studentName}</strong>?
              </p>
              <p className="text-sm text-destructive-action-dark mt-2">
                {translate("This is a GDPR-compliant operation which will permanently destroy all data on file for this Student.")}
              </p>
            </div>

            <div className="space-y-3">
              <div className="bg-orange-50 p-3 rounded-lg border border-orange-200">
                <h4 className="font-medium text-orange-900 mb-2">{translate("Deleting a Student will also delete")}</h4>
                <ul className="ml-4 space-y-1">
                  <li className="text-sm text-orange-800">• their login account (if they have one)</li>
                  <li className="text-sm text-orange-800">• {translate("their Shared Assessment links")}</li>
                  <li className="text-sm text-orange-800">• {translate("their Assessment Results")}</li>
                  <li className="text-sm text-orange-800">• their curriculum</li>
                  <li className="text-sm text-orange-800">• their Completed Units</li>
                </ul>
              </div>

              <div className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border">
                • {translate("If you wish to retain any of this students data, use Archive instead")}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-5">
            <button
              onClick={close}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {translate("Cancel")}
            </button>
            <button
              onClick={handleDelete}
              className="px-6 py-2 bg-destructive-action hover:bg-destructive-action-dark text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isDeleting}
            >
              <Trash2 size={16} className={`mr-2 ${isDeleting ? 'animate-spin' : ''}`} />
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
